import { useMemo } from 'react';
import { CopilotImportStrategy } from '../CopilotSetting.types';
import { CopilotImportStrategyFormState } from './useCopilotImportStrategyForm';
import { isEqual } from 'lodash';
import { COPILIT_SETTING_SELECTION_TYPES } from '../definitions';
import { lbToast } from '@linearb/buzz';

interface UseSaveHandlerProps {
  settingValue: CopilotImportStrategy;
  onSaveSetting: (payload: CopilotImportStrategy) => Promise<void>;
  formState: CopilotImportStrategyFormState;
}

interface UseSaveHandlerReturn {
  isSaveDisabled: boolean;
  onSave: () => void;
}

const useSaveHandler = ({
  settingValue,
  onSaveSetting,
  formState
}: UseSaveHandlerProps): UseSaveHandlerReturn => {
  const newSettingValue = useMemo(() => {
    return {
      selection_type: formState.selectionMethod,
      ...(formState.selectionMethod ===
        COPILIT_SETTING_SELECTION_TYPES.REGEX && {
        regex: formState.regexPattern
      }),
      ...(formState.selectionMethod ===
        COPILIT_SETTING_SELECTION_TYPES.CUSTOM && {
        organizations: formState.selectedOrgs
      })
    };
  }, [formState]);

  const isSaveDisabled = useMemo(
    () =>
      isEqual(newSettingValue, settingValue) || !newSettingValue.selection_type,
    [newSettingValue, settingValue]
  );

  const validateForm = (): boolean => {
    try {
      // Validation for REGEX method
      if (formState.selectionMethod === COPILIT_SETTING_SELECTION_TYPES.REGEX) {
        if (!formState.regexPattern || formState.regexPattern.trim() === '') {
          throw new Error('Regex pattern cannot be empty');
        }

        try {
          new RegExp(formState.regexPattern);
        } catch (error) {
          throw new Error('Invalid regex pattern. Please check your syntax.');
        }
      }

      // Validation for CUSTOM method
      if (
        formState.selectionMethod === COPILIT_SETTING_SELECTION_TYPES.CUSTOM
      ) {
        if (!formState.selectedOrgs || formState.selectedOrgs.length === 0) {
          throw new Error('At least one organization must be selected');
        }
      }

      return true;
    } catch (error) {
      lbToast({
        title: 'Validation Failed',
        description: error.message,
        variant: 'destructive'
      });
      return false;
    }
  };

  const onSave = async () => {
    const isFormValid = validateForm();
    if (!isFormValid) return;

    try {
      await onSaveSetting(newSettingValue);

      lbToast({
        title: '',
        description: 'Copilot settings have been saved successfully',
        variant: 'success'
      });
    } catch (error) {
      lbToast({
        title: 'Save Failed',
        description: 'Failed to save the Copilot settings',
        variant: 'destructive'
      });
    }
  };

  return {
    isSaveDisabled,
    onSave
  };
};

export default useSaveHandler;
